import React, { useState, useEffect } from 'react';
import { userAPI, authAPI } from '../services/api';
import { 
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  CreditCardIcon,
  LockClosedIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon,
  ChartBarSquareIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Profile Settings
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const [profileUpdating, setProfileUpdating] = useState(false);
  
  // Security Settings
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordUpdating, setPasswordUpdating] = useState(false);
  
  // Wallet Management
  const [wallets, setWallets] = useState([]);
  const [newWallet, setNewWallet] = useState({
    type: 'crypto',
    name: '',
    address: '',
    network: 'ERC20',
    currency: 'USDT',
    isDefault: false,
  });
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [editingWalletId, setEditingWalletId] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchProfile();
    fetchWallets();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getProfile();
      setProfile(response.data);
      setProfileForm({
        fullName: response.data.user.fullName || '',
        email: response.data.user.email || '',
        phone: response.data.user.phone || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWallets = async () => {
    try {
      const response = await userAPI.getWallets();
      if (response.data.success) {
        setWallets(response.data.wallets || []);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileUpdating(true);
    try {
      await userAPI.updateProfile(profileForm);
      alert('✅ Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    if (securityForm.newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    setPasswordUpdating(true);
    try {
      await userAPI.changePassword({
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword,
      });
      alert('✅ Password changed successfully!');
      setSecurityForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordUpdating(false);
    }
  };

  const handleAddWallet = async (e) => {
    e.preventDefault();
    
    if (!newWallet.name || !newWallet.address) {
      alert('Wallet name and address are required');
      return;
    }

    setWalletLoading(true);
    try {
      if (editingWalletId) {
        await userAPI.updateWallet(editingWalletId, newWallet);
        alert('✅ Wallet updated successfully!');
      } else {
        await userAPI.addWallet(newWallet);
        alert('✅ Wallet added successfully!');
      }
      
      setNewWallet({
        type: 'crypto',
        name: '',
        address: '',
        network: 'ERC20',
        currency: 'USDT',
        isDefault: false,
      });
      setShowWalletForm(false);
      setEditingWalletId(null);
      fetchWallets();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save wallet');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleDeleteWallet = async (walletId) => {
    if (!window.confirm('Are you sure you want to delete this wallet?')) {
      return;
    }

    try {
      await userAPI.deleteWallet(walletId);
      alert('✅ Wallet deleted successfully!');
      fetchWallets();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete wallet');
    }
  };

  const handleSetDefaultWallet = async (walletId) => {
    try {
      await userAPI.setDefaultWallet(walletId);
      alert('✅ Default wallet updated!');
      fetchWallets();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to set default wallet');
    }
  };

  const handleEditWallet = (wallet) => {
    setNewWallet(wallet);
    setEditingWalletId(wallet.id);
    setShowWalletForm(true);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      authAPI.logout();
      window.location.href = '/login';
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 212, 255, 0.2)',
          borderTop: '4px solid #00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px 16px 100px 16px' : '20px 20px 40px 20px',
      minHeight: '100vh',
      backgroundColor: '#0a0a0a'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        display: 'flex',
        alignItems: isMobile ? 'center' : 'flex-start',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '20px',
        marginBottom: '30px',
        textAlign: isMobile ? 'center' : 'left'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: '#00d4ff',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <UserCircleIcon style={{ width: '60px', height: '60px', color: '#1a1a2e' }} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{
            fontSize: isMobile ? '24px' : '28px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #fff, #00d4ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: '0 0 8px 0'
          }}>{profile?.user?.fullName || profile?.user?.username}</h1>
          <p style={{
            color: '#94a3b8',
            margin: '0 0 12px 0',
            fontSize: '14px'
          }}>{profile?.user?.email || 'No email set'}</p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(0, 212, 255, 0.1)',
            padding: '6px 12px',
            borderRadius: '20px'
          }}>
            <ShieldCheckIcon style={{ width: '16px', height: '16px', color: '#00d4ff' }} />
            <span style={{ fontSize: '13px', color: '#00d4ff' }}>Verified Account</span>
          </div>
        </div>
      </div>

      {/* Stats - Dark Theme */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: '#1a1a2e',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <CurrencyDollarIcon style={{ width: '32px', height: '32px', color: '#00d4ff' }} />
          <div>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Balance</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#00d4ff', margin: 0 }}>${profile?.user?.balance?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
        
        <div style={{
          background: '#1a1a2e',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <ChartBarIcon style={{ width: '32px', height: '32px', color: '#f59e0b' }} />
          <div>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Active Plans</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: 'white', margin: 0 }}>{profile?.stats?.activePlans || 0}</p>
          </div>
        </div>
        
        <div style={{
          background: '#1a1a2e',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <UserGroupIcon style={{ width: '32px', height: '32px', color: '#8b5cf6' }} />
          <div>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Referrals</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: 'white', margin: 0 }}>{profile?.user?.referralCount || 0}</p>
          </div>
        </div>
      </div>

      {/* Mobile Tabs Dropdown - Dark Theme */}
      {isMobile && (
        <div style={{ marginBottom: '20px' }}>
          <select 
            value={activeTab} 
            onChange={(e) => setActiveTab(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              background: '#1a1a2e',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="overview" style={{ background: '#1a1a2e' }}>📊 Overview</option>
            <option value="wallets" style={{ background: '#1a1a2e' }}>💳 Crypto Wallets</option>
            <option value="settings" style={{ background: '#1a1a2e' }}>⚙️ Settings</option>
          </select>
        </div>
      )}

      {/* Desktop Tabs - Dark Theme */}
      {!isMobile && (
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '32px',
          borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
          paddingBottom: '12px'
        }}>
          {[
            { id: 'overview', label: 'Overview', icon: ChartBarSquareIcon },
            { id: 'wallets', label: 'Crypto Wallets', icon: CreditCardIcon },
            { id: 'settings', label: 'Settings', icon: Cog6ToothIcon }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: isActive ? '#00d4ff' : 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  color: isActive ? '#1a1a2e' : '#94a3b8',
                  fontSize: '14px',
                  fontWeight: isActive ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <Icon style={{ width: '18px', height: '18px' }} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Overview Tab - Dark Theme */}
      {activeTab === 'overview' && (
        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {/* Personal Information */}
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#00d4ff',
              marginBottom: '20px'
            }}>Personal Information</h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: '#0f0f0f',
                borderRadius: '12px'
              }}>
                <UserCircleIcon style={{ width: '20px', height: '20px', color: '#00d4ff' }} />
                <div>
                  <span style={{ display: 'block', fontSize: '12px', color: '#94a3b8' }}>Username</span>
                  <span style={{ color: 'white', fontSize: '14px' }}>{profile?.user?.username}</span>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: '#0f0f0f',
                borderRadius: '12px'
              }}>
                <EnvelopeIcon style={{ width: '20px', height: '20px', color: '#00d4ff' }} />
                <div>
                  <span style={{ display: 'block', fontSize: '12px', color: '#94a3b8' }}>Email</span>
                  <span style={{ color: 'white', fontSize: '14px' }}>{profile?.user?.email || 'Not set'}</span>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: '#0f0f0f',
                borderRadius: '12px'
              }}>
                <PhoneIcon style={{ width: '20px', height: '20px', color: '#00d4ff' }} />
                <div>
                  <span style={{ display: 'block', fontSize: '12px', color: '#94a3b8' }}>Phone</span>
                  <span style={{ color: 'white', fontSize: '14px' }}>{profile?.user?.phone || 'Not set'}</span>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: '#0f0f0f',
                borderRadius: '12px'
              }}>
                <CalendarIcon style={{ width: '20px', height: '20px', color: '#00d4ff' }} />
                <div>
                  <span style={{ display: 'block', fontSize: '12px', color: '#94a3b8' }}>Joined</span>
                  <span style={{ color: 'white', fontSize: '14px' }}>
                    {profile?.user?.joinedDate ? 
                      new Date(profile?.user?.joinedDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      }) : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Statistics - Dark Theme */}
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#00d4ff',
              marginBottom: '20px'
            }}>Account Statistics</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '16px'
            }}>
              <div style={{
                background: 'rgba(0, 212, 255, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>Total Deposited</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#00d4ff', margin: 0 }}>${profile?.stats?.totalDeposited?.toFixed(2) || '0.00'}</p>
              </div>
              <div style={{
                background: 'rgba(239, 68, 68, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}>
                <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>Total Withdrawn</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444', margin: 0 }}>${profile?.stats?.totalWithdrawn?.toFixed(2) || '0.00'}</p>
              </div>
              <div style={{
                background: 'rgba(245, 158, 11, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid rgba(245, 158, 11, 0.2)'
              }}>
                <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>Total Profit</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b', margin: 0 }}>${profile?.stats?.totalProfit?.toFixed(2) || '0.00'}</p>
              </div>
              <div style={{
                background: 'rgba(0, 212, 255, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>Active Investments</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#00d4ff', margin: 0 }}>{profile?.stats?.activePlans || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallets Tab - Dark Theme */}
      {activeTab === 'wallets' && (
        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {/* Add/Edit Wallet Form */}
          {(showWalletForm || editingWalletId) && (
            <div style={{
              background: '#1a1a2e',
              borderRadius: '24px',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              padding: '24px',
              marginBottom: '24px',
              animation: 'slideDown 0.3s ease-out'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#00d4ff',
                  margin: 0
                }}>{editingWalletId ? 'Edit Wallet' : 'Add New Wallet'}</h3>
                <button
                  onClick={() => {
                    setShowWalletForm(false);
                    setEditingWalletId(null);
                    setNewWallet({
                      type: 'crypto',
                      name: '',
                      address: '',
                      network: 'ERC20',
                      currency: 'USDT',
                      isDefault: false,
                    });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8',
                    padding: '8px'
                  }}
                >
                  <XMarkIcon style={{ width: '20px', height: '20px' }} />
                </button>
              </div>

              <form onSubmit={handleAddWallet}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Wallet Name *</label>
                  <input
                    type="text"
                    value={newWallet.name}
                    onChange={(e) => setNewWallet({...newWallet, name: e.target.value})}
                    placeholder="e.g., My USDT Wallet"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Wallet Address *</label>
                  <textarea
                    value={newWallet.address}
                    onChange={(e) => setNewWallet({...newWallet, address: e.target.value})}
                    placeholder="Enter crypto wallet address"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                    required
                  />
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Double-check the address before saving</p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Network</label>
                    <select
                      value={newWallet.network}
                      onChange={(e) => setNewWallet({...newWallet, network: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#0f0f0f',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    >
                      <option value="ERC20">ERC20 (Ethereum)</option>
                      <option value="BEP20">BEP20 (Binance Smart Chain)</option>
                      <option value="TRC20">TRC20 (Tron)</option>
                      <option value="SOL">Solana</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Currency</label>
                    <select
                      value={newWallet.currency}
                      onChange={(e) => setNewWallet({...newWallet, currency: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#0f0f0f',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    >
                      <option value="USDT">USDT</option>
                      <option value="BTC">Bitcoin (BTC)</option>
                      <option value="ETH">Ethereum (ETH)</option>
                      <option value="SOL">Solana (SOL)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newWallet.isDefault}
                      onChange={(e) => setNewWallet({...newWallet, isDefault: e.target.checked})}
                      style={{ width: '18px', height: '18px', accentColor: '#00d4ff' }}
                    />
                    <span style={{ fontSize: '14px', color: '#e2e8f0' }}>Set as default withdrawal wallet</span>
                  </label>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid rgba(0, 212, 255, 0.2)'
                }}>
                  <button
                    type="submit"
                    disabled={walletLoading}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: walletLoading ? '#334155' : '#00d4ff',
                      border: 'none',
                      borderRadius: '12px',
                      color: walletLoading ? '#94a3b8' : '#1a1a2e',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: walletLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {walletLoading ? 'Saving...' : (editingWalletId ? 'Update Wallet' : 'Add Wallet')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowWalletForm(false);
                      setEditingWalletId(null);
                      setNewWallet({
                        type: 'crypto',
                        name: '',
                        address: '',
                        network: 'ERC20',
                        currency: 'USDT',
                        isDefault: false,
                      });
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: '#94a3b8',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Wallets Header */}
          {!showWalletForm && !editingWalletId && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#00d4ff',
                margin: 0
              }}>Crypto Wallets</h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={fetchWallets}
                  style={{
                    padding: '10px 16px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '10px',
                    color: '#00d4ff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
                  <span>Refresh</span>
                </button>
                <button
                  onClick={() => setShowWalletForm(true)}
                  style={{
                    padding: '10px 20px',
                    background: '#00d4ff',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#1a1a2e',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600'
                  }}
                >
                  <PlusIcon style={{ width: '16px', height: '16px' }} />
                  <span>Add Wallet</span>
                </button>
              </div>
            </div>
          )}

          {/* Wallets List */}
          {!showWalletForm && !editingWalletId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {wallets.length > 0 ? (
                wallets.map((wallet) => (
                  <div key={wallet.id} style={{
                    background: '#1a1a2e',
                    borderRadius: '20px',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    padding: '20px',
                    transition: 'transform 0.2s'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: '#00d4ff',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CreditCardIcon style={{ width: '24px', height: '24px', color: '#1a1a2e' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          flexWrap: 'wrap',
                          marginBottom: '8px'
                        }}>
                          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>{wallet.name}</h4>
                          {wallet.isDefault && (
                            <span style={{
                              background: 'rgba(0, 212, 255, 0.2)',
                              color: '#00d4ff',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>Default</span>
                          )}
                        </div>
                        <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 8px 0' }}>
                          {wallet.currency} • {wallet.network}
                        </p>
                        <p style={{
                          background: '#0f0f0f',
                          padding: '12px',
                          borderRadius: '10px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color: '#94a3b8',
                          wordBreak: 'break-all',
                          margin: '12px 0'
                        }}>
                          {wallet.address}
                        </p>
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => handleEditWallet(wallet)}
                            style={{
                              padding: '8px 16px',
                              background: 'rgba(0, 212, 255, 0.2)',
                              border: '1px solid rgba(0, 212, 255, 0.3)',
                              borderRadius: '8px',
                              color: '#00d4ff',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Edit
                          </button>
                          {!wallet.isDefault && (
                            <button
                              onClick={() => handleSetDefaultWallet(wallet.id)}
                              style={{
                                padding: '8px 16px',
                                background: 'rgba(16, 185, 129, 0.2)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '8px',
                                color: '#34d399',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteWallet(wallet.id)}
                            style={{
                              padding: '8px 16px',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              borderRadius: '8px',
                              color: '#f87171',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  background: '#1a1a2e',
                  borderRadius: '24px',
                  border: '1px solid rgba(0, 212, 255, 0.2)'
                }}>
                  <CreditCardIcon style={{ width: '64px', height: '64px', color: '#64748b', margin: '0 auto 16px' }} />
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: 'white', marginBottom: '8px' }}>No Crypto Wallets</h3>
                  <p style={{ color: '#94a3b8', marginBottom: '20px' }}>Add a crypto wallet to receive withdrawals</p>
                  <button
                    onClick={() => setShowWalletForm(true)}
                    style={{
                      padding: '12px 24px',
                      background: '#00d4ff',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#1a1a2e',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <PlusIcon style={{ width: '16px', height: '16px' }} />
                    Add First Wallet
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Security Note */}
          {!showWalletForm && !editingWalletId && (
            <div style={{
              marginTop: '24px',
              padding: '20px',
              background: 'rgba(0, 212, 255, 0.1)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              display: 'flex',
              gap: '12px'
            }}>
              <ShieldCheckIcon style={{ width: '20px', height: '20px', color: '#00d4ff', flexShrink: 0 }} />
              <div>
                <p style={{ color: '#00d4ff', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>Security Warning</p>
                <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Your wallet addresses are encrypted and securely stored. Never share your private keys. Always double-check wallet addresses before withdrawals.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab - Dark Theme */}
      {activeTab === 'settings' && (
        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {/* Profile Settings */}
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '20px',
              fontWeight: '600',
              color: '#00d4ff',
              marginBottom: '20px'
            }}>
              <UserCircleIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
              Profile Settings
            </h3>
            <form onSubmit={handleProfileUpdate}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Full Name</label>
                <input
                  type="text"
                  value={profileForm.fullName}
                  onChange={(e) => setProfileForm({...profileForm, fullName: e.target.value})}
                  placeholder="Enter your full name"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Email Address</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Phone Number</label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  placeholder="Enter your phone number"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={profileUpdating}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: profileUpdating ? '#334155' : '#00d4ff',
                  border: 'none',
                  borderRadius: '12px',
                  color: profileUpdating ? '#94a3b8' : '#1a1a2e',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: profileUpdating ? 'not-allowed' : 'pointer'
                }}
              >
                {profileUpdating ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          </div>

          {/* Security Settings */}
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '20px',
              fontWeight: '600',
              color: '#00d4ff',
              marginBottom: '20px'
            }}>
              <LockClosedIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
              Security Settings
            </h3>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Current Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={securityForm.currentPassword}
                    onChange={(e) => setSecurityForm({...securityForm, currentPassword: e.target.value})}
                    placeholder="Enter current password"
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 12px',
                      background: '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8'
                    }}
                  >
                    {showCurrentPassword ? <EyeSlashIcon style={{ width: '18px', height: '18px' }} /> : <EyeIcon style={{ width: '18px', height: '18px' }} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={securityForm.newPassword}
                    onChange={(e) => setSecurityForm({...securityForm, newPassword: e.target.value})}
                    placeholder="Enter new password"
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 12px',
                      background: '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8'
                    }}
                  >
                    {showNewPassword ? <EyeSlashIcon style={{ width: '18px', height: '18px' }} /> : <EyeIcon style={{ width: '18px', height: '18px' }} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#e2e8f0', marginBottom: '8px' }}>Confirm New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={securityForm.confirmPassword}
                    onChange={(e) => setSecurityForm({...securityForm, confirmPassword: e.target.value})}
                    placeholder="Confirm new password"
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 12px',
                      background: '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8'
                    }}
                  >
                    {showConfirmPassword ? <EyeSlashIcon style={{ width: '18px', height: '18px' }} /> : <EyeIcon style={{ width: '18px', height: '18px' }} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={passwordUpdating}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: passwordUpdating ? '#334155' : '#00d4ff',
                  border: 'none',
                  borderRadius: '12px',
                  color: passwordUpdating ? '#94a3b8' : '#1a1a2e',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: passwordUpdating ? 'not-allowed' : 'pointer'
                }}
              >
                {passwordUpdating ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>

          {/* Danger Zone */}
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '24px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: '24px'
          }}>
            <h3 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '20px',
              fontWeight: '600',
              color: '#f87171',
              marginBottom: '20px'
            }}>
              <QuestionMarkCircleIcon style={{ width: '24px', height: '24px' }} />
              Danger Zone
            </h3>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '12px',
                  color: '#f87171',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontWeight: '600'
                }}
              >
                <ArrowRightOnRectangleIcon style={{ width: '18px', height: '18px' }} />
                Logout
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure? This will permanently delete your account and all data.')) {
                    alert('Account deletion requested. Please contact support.');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '12px',
                  color: '#f87171',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontWeight: '600'
                }}
              >
                <TrashIcon style={{ width: '18px', height: '18px' }} />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default Profile;